/**
 * SurveyCharts.js — Construtores de gráficos Highcharts do Diagnóstico Participativo.
 *
 * Compartilhado entre a apresentação (SurveyDashboard.vue) e a captura de gráficos usada
 * ao gerar o Capítulo 3 do PMAU com IA (ParticipatoryDiagnosisCharts.js) — garante que o
 * gráfico exibido no capítulo gerado seja pixel a pixel o mesmo da apresentação, já que
 * ambos usam exatamente a mesma configuração Highcharts.
 */

export const ARBORAN_GREEN = "#2E7D32";
export const SCALE_COLORS = ["#E53935", "#FF8F00", "#FDD835", "#7CB342", "#2E7D32"];
export const CHART_FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const DEFAULT_CHART_H = 420;

export function buildDonutChart(question, chartH = DEFAULT_CHART_H) {
  const dist = question.distribution || [];
  let colors = ["#1B5E20", "#2E7D32", "#43A047", "#66BB6A", "#A5D6A7"];

  if (question.code === "eval_classification") {
    colors = ["#E53935", "#FF8F00", "#2E7D32"];
  } else if (question.code === "eval_more_trees") {
    colors = ["#2E7D32", "#9E9E9E", "#E53935"];
  }

  return {
    chart: { type: "pie", height: chartH, backgroundColor: null, style: { fontFamily: CHART_FONT } },
    title: { text: null },
    tooltip: {
      style: { fontSize: "15px", fontFamily: CHART_FONT },
      formatter() {
        return `<b>${this.point.name}</b><br>${this.y} respostas (${this.percentage.toFixed(1)}%)`;
      },
    },
    plotOptions: {
      pie: {
        innerSize: "55%",
        dataLabels: {
          enabled: true,
          format: "<b>{point.name}</b><br>{point.percentage:.0f}%",
          style: { fontSize: "16px", fontWeight: "600" },
        },
        showInLegend: false,
      },
    },
    colors,
    series: [{ name: "Respostas", data: dist.map(d => ({ name: d.option_text, y: d.count })) }],
    legend: { enabled: false },
    credits: { enabled: false },
  };
}

/**
 * `fit: true` (apresentação): o gráfico ocupa exatamente `chartH`, com todas as opções
 * ordenadas por quantidade — fontes e espaçamento encolhem para caber sem scroll.
 * Sem `fit` (captura do Capítulo 3): até 12 opções e altura mínima de 66 px por barra.
 */
export function buildBarChart(question, chartH = DEFAULT_CHART_H, { fit = false } = {}) {
  let dist = [...(question.distribution || [])];

  if (fit || question.question_type === "multiple_choice") {
    dist.sort((a, b) => b.count - a.count);
  }
  if (!fit && dist.length > 12) dist = dist.slice(0, 12);

  const height = fit ? chartH : Math.max(chartH, dist.length * 66 + 80);

  // Espaço por barra descontando o eixo inferior (~50 px); a 66 px/barra vale o tamanho padrão.
  const rowH = (height - 50) / Math.max(1, dist.length);
  const scale = fit ? Math.min(1, rowH / 66) : 1;
  const px = (base, min) => Math.max(min, Math.round(base * scale)) + "px";

  return {
    chart: { type: "bar", height, backgroundColor: null, style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: {
      categories: dist.map(d => d.option_text),
      // step 1: com barras espremidas o Highcharts pularia rótulos de bairro
      labels: { ...(fit ? { step: 1 } : {}), style: { fontSize: px(16, 10), color: "#333", fontFamily: CHART_FONT } },
    },
    yAxis: {
      title: null,
      labels: { format: "{value}%", style: { fontSize: "14px" } },
      max: 100,
      gridLineColor: "#eee",
    },
    plotOptions: {
      bar: {
        borderRadius: scale < 0.5 ? 2 : 4,
        ...(scale < 1 ? { pointPadding: 0.05, groupPadding: 0.05 } : {}),
        dataLabels: {
          enabled: true,
          format: "{y:.0f}%",
          style: { fontSize: px(17, 10), fontWeight: "700", color: "#fff", textOutline: "1px rgba(0,0,0,0.2)" },
          inside: true,
          align: "right",
        },
      },
    },
    series: [{
      name: "Respostas",
      data: dist.map(d => parseFloat(d.percentage.toFixed(1))),
      color: ARBORAN_GREEN,
    }],
    legend: { enabled: false },
    credits: { enabled: false },
  };
}

export function buildLikertChart(question, chartH = DEFAULT_CHART_H) {
  const dist = question.distribution || [];
  const LIKERT_COLORS = ["#E53935", "#9E9E9E", "#2E7D32"];

  return {
    chart: { type: "bar", height: Math.round(chartH * 0.35), backgroundColor: null, style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: { categories: [""], labels: { enabled: false }, lineWidth: 0, tickLength: 0 },
    yAxis: { title: null, labels: { format: "{value}%", style: { fontSize: "14px", fontFamily: CHART_FONT } }, gridLineWidth: 0 },
    plotOptions: {
      bar: {
        stacking: "percent",
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
          style: { fontSize: "19px", fontWeight: "700", color: "#fff", textOutline: "1px rgba(0,0,0,0.3)" },
        },
      },
    },
    series: dist.map((d, i) => ({
      name: d.option_text,
      data: [d.count],
      color: LIKERT_COLORS[i] !== undefined ? LIKERT_COLORS[i] : "#888",
    })),
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      reversed: true,
      itemStyle: { fontSize: "15px", fontWeight: "400", fontFamily: CHART_FONT },
    },
    tooltip: {
      style: { fontSize: "15px", fontFamily: CHART_FONT },
      formatter() {
        return `<b>${this.series.name}</b>: ${this.y} (${this.percentage.toFixed(0)}%)`;
      },
    },
    credits: { enabled: false },
  };
}

export function buildGaugeChart(question, chartH = DEFAULT_CHART_H) {
  const dist = question.distribution || [];
  return {
    chart: { type: "column", height: chartH, backgroundColor: null, style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: {
      categories: dist.map(d => String(d.value)),
      title: null,
      gridLineWidth: 0,
      labels: { style: { fontSize: "16px", fontFamily: CHART_FONT } },
    },
    yAxis: { title: null, gridLineColor: "#eee", labels: { style: { fontSize: "14px", fontFamily: CHART_FONT } } },
    plotOptions: {
      column: {
        borderRadius: 4,
        dataLabels: {
          enabled: true,
          format: "{y}",
          style: { fontSize: "16px", fontWeight: "700", textOutline: "none" },
        },
      },
    },
    series: [{
      name: "Respostas",
      data: dist.map((d, i) => ({
        y: d.count,
        color: SCALE_COLORS[Math.min(i, SCALE_COLORS.length - 1)],
      })),
    }],
    legend: { enabled: false },
    credits: { enabled: false },
  };
}

export function buildProfileDonut(question) {
  const dist = (question.distribution || []).filter(d => d.count > 0);
  const COLORS = ["#1565C0", "#D81B60", "#7B1FA2", "#9E9E9E"];
  return {
    chart: { type: "pie", height: 280, backgroundColor: null, style: { fontFamily: CHART_FONT } },
    title: { text: null },
    plotOptions: {
      pie: {
        innerSize: "50%",
        dataLabels: {
          enabled: true,
          format: "{point.percentage:.0f}%",
          style: { fontSize: "15px", fontWeight: "600", fontFamily: CHART_FONT },
        },
        showInLegend: true,
      },
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: { fontSize: "13px", fontFamily: CHART_FONT },
    },
    colors: COLORS,
    series: [{ name: "Respostas", data: dist.map(d => ({ name: d.option_text, y: d.count })) }],
    credits: { enabled: false },
  };
}

export function buildProfileBar(question) {
  const dist = question.distribution || [];
  const height = Math.max(240, dist.length * 42 + 70);
  return {
    chart: { type: "bar", height, backgroundColor: null, style: { fontFamily: CHART_FONT } },
    title: { text: null },
    xAxis: {
      categories: dist.map(d => d.option_text),
      labels: { style: { fontSize: "14px", fontFamily: CHART_FONT } },
    },
    yAxis: { title: null, labels: { format: "{value}%", style: { fontSize: "13px", fontFamily: CHART_FONT } }, max: 100, gridLineColor: "#eee" },
    plotOptions: {
      bar: {
        borderRadius: 3,
        dataLabels: {
          enabled: true,
          format: "{y:.0f}%",
          style: { fontSize: "14px", fontWeight: "700", textOutline: "none" },
          inside: false,
        },
      },
    },
    series: [{
      name: "Respostas",
      data: dist.map(d => parseFloat(d.percentage.toFixed(1))),
      color: ARBORAN_GREEN,
    }],
    legend: { enabled: false },
    credits: { enabled: false },
  };
}

/**
 * Perguntas de identificação combinadas no slide/gráfico de perfil sociodemográfico.
 * chartStyle indica qual construtor usar (donut ou bar) para cada pergunta.
 */
export const PROFILE_QUESTION_CONFIGS = [
  { code: "id_genero", chartStyle: "donut" },
  { code: "id_faixa_etaria", chartStyle: "bar" },
  { code: "id_escolaridade", chartStyle: "bar" },
];

/**
 * Mapeamento código de pergunta → tipo de gráfico, para as perguntas de avaliação
 * (categoria "evaluation") e id_bairro. Usado tanto para montar os slides da apresentação
 * quanto para resolver os marcadores [GRAFICO_*] do Capítulo 3 do PMAU.
 */
export const CHART_TYPE_BY_CODE = {
  id_bairro: "bar_h",
  eval_classification: "donut",
  eval_beauty_scale: "gauge",
  eval_mental_health: "bar_h",
  eval_problems: "bar_h",
  eval_maintenance: "bar_h",
  eval_investment_likert: "likert",
  eval_urban_trees: "likert",
  eval_wishes: "bar_h",
  eval_more_trees: "donut",
};

/**
 * Constrói as Highcharts options para uma pergunta, dado o tipo de gráfico ("donut",
 * "bar_h", "gauge" ou "likert"). Usado pela captura de gráficos do Capítulo 3.
 */
export function buildChartOptions(chartType, question, chartH = DEFAULT_CHART_H) {
  switch (chartType) {
    case "donut": return buildDonutChart(question, chartH);
    case "bar_h": return buildBarChart(question, chartH);
    case "gauge": return buildGaugeChart(question, chartH);
    case "likert": return buildLikertChart(question, chartH);
    default: return null;
  }
}
